import { useEffect, useMemo, useState } from 'react';
import { Play, Square } from 'lucide-react';
import { Quiz } from './Quiz';
import { useNarration } from '@/hooks/useNarration';
import type { EducateUnit } from '@/utils/units';
import type { EducateInteraction } from '@/types/educate';

interface UnitRendererProps {
  unit: EducateUnit;
  narrationEnabled: boolean;
  onInteractions: (interactions: EducateInteraction[]) => void;
  onBranch: (nextUnitId: string | null) => void;
  onGateChange: (canAdvance: boolean) => void;
}

const Prose = ({ children }: { children: string }) => (
  <p className="whitespace-pre-line leading-relaxed text-slate-700 dark:text-slate-300">
    {children}
  </p>
);

export function UnitRenderer({
  unit,
  narrationEnabled,
  onInteractions,
  onBranch,
  onGateChange
}: UnitRendererProps) {
  const [answers, setAnswers] = useState<Record<string, EducateInteraction>>({});

  const quizCount = unit.kind === 'reading' ? unit.quiz.length : unit.kind === 'check' ? 1 : 0;

  const narrationText =
    unit.kind === 'slide' ? unit.script : unit.kind === 'podcastTurn' ? unit.text : '';
  const narration = useNarration(narrationText, narrationEnabled && Boolean(narrationText));

  useEffect(() => {
    setAnswers({});
  }, [unit.id]);

  const interactions = useMemo<EducateInteraction[]>(() => Object.values(answers), [answers]);

  const quizAnswered = useMemo(
    () => interactions.filter((i) => i.kind === 'quiz').length,
    [interactions]
  );

  useEffect(() => {
    onInteractions(interactions);
    onGateChange(quizAnswered >= quizCount);
  }, [interactions, quizAnswered, quizCount, onInteractions, onGateChange]);

  const record = ({
    unitId,
    answer,
    isCorrect
  }: {
    unitId: string;
    answer: number;
    isCorrect: boolean;
  }) =>
    setAnswers((prev) => ({
      ...prev,
      [unitId]: { unitId, kind: 'quiz', answer, isCorrect }
    }));

  const recordChoice = (interaction: EducateInteraction) =>
    setAnswers((prev) => ({ ...prev, [interaction.unitId]: interaction }));

  switch (unit.kind) {
    case 'reading':
      return (
        <section className="space-y-6">
          <div className="space-y-4">
            {unit.paragraphs.map((paragraph, index) => (
              <Prose key={index}>{paragraph}</Prose>
            ))}
          </div>

          {unit.flashcards.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2">
              {unit.flashcards.map((card) => (
                <div
                  key={card.term}
                  className="rounded-lg bg-slate-50 p-4 dark:bg-slate-900/60"
                  data-testid="flashcard"
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">
                    {card.term}
                  </p>
                  <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                    {card.definition}
                  </p>
                </div>
              ))}
            </div>
          )}

          {unit.quiz.map((question, index) => (
            <Quiz
              key={index}
              unitId={unit.id}
              index={index}
              question={question}
              onAnswer={record}
            />
          ))}
        </section>
      );

    case 'prose':
      return <Prose>{unit.body}</Prose>;

    case 'list':
      return (
        <ul className="list-disc space-y-2 pl-5 text-slate-700 dark:text-slate-300">
          {unit.items.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      );

    case 'beat':
      return (
        <section className="space-y-3">
          <p className="text-lg font-medium">{unit.point}</p>
          <Prose>{unit.explanation}</Prose>
        </section>
      );

    case 'hypothesis':
      return (
        <section className="space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Possible cause
            </p>
            <p className="mt-1 text-lg font-medium">{unit.cause}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              How to test it
            </p>
            <Prose>{unit.test}</Prose>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Verdict</p>
            <Prose>{unit.reasoning}</Prose>
          </div>
          {unit.isRootCause && (
            <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
              This is the root cause.
            </p>
          )}
        </section>
      );

    case 'check':
      return <Quiz unitId={unit.id} index={0} question={unit.question} onAnswer={record} />;

    case 'casePage':
      return (
        <section className="space-y-5">
          <Prose>{unit.narrative}</Prose>
          <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-800">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">
              Your analysis
            </p>
            <Prose>{unit.analysis}</Prose>
            <details className="mt-3">
              <summary className="cursor-pointer text-sm font-medium text-brand-600">
                Show a model answer
              </summary>
              <div className="mt-2">
                <Prose>{unit.modelAnswer}</Prose>
              </div>
            </details>
          </div>
        </section>
      );

    case 'slide':
      return (
        <section className="space-y-5">
          <ul className="space-y-2">
            {unit.bullets.map((bullet, index) => (
              <li key={index} className="flex gap-2 text-slate-700 dark:text-slate-300">
                <span className="text-brand-500">•</span>
                <span>{bullet}</span>
              </li>
            ))}
          </ul>

          {unit.script && (
            <div className="rounded-lg bg-slate-50 p-4 dark:bg-slate-900/60">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Narration
                </p>
                {narration.supported && (
                  <button
                    type="button"
                    onClick={narration.toggle}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium dark:border-slate-700"
                  >
                    {narration.isSpeaking ? <Square size={12} /> : <Play size={12} />}
                    {narration.isSpeaking ? 'Stop' : 'Listen'}
                  </button>
                )}
              </div>
              <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">{unit.script}</p>
            </div>
          )}
        </section>
      );

    case 'podcastTurn':
      return (
        <section className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                unit.speaker === 'host'
                  ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
                  : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
              }`}
            >
              {unit.speaker}
            </span>
            {narration.supported && (
              <button
                type="button"
                onClick={narration.toggle}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium dark:border-slate-700"
              >
                {narration.isSpeaking ? <Square size={12} /> : <Play size={12} />}
                {narration.isSpeaking ? 'Stop' : 'Listen'}
              </button>
            )}
          </div>
          <Prose>{unit.text}</Prose>
        </section>
      );

    case 'decisionNode':
      return (
        <section className="space-y-5">
          <Prose>{unit.situation}</Prose>

          {unit.terminal ? (
            <div className="rounded-lg bg-slate-50 p-4 dark:bg-slate-900/60">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Outcome
              </p>
              <Prose>{unit.outcome ?? ''}</Prose>
            </div>
          ) : (
            <DecisionChoices unit={unit} onBranch={onBranch} onChoice={recordChoice} />
          )}
        </section>
      );

    default:
      return null;
  }
}

function DecisionChoices({
  unit,
  onBranch,
  onChoice
}: {
  unit: Extract<EducateUnit, { kind: 'decisionNode' }>;
  onBranch: (nextUnitId: string | null) => void;
  onChoice: (interaction: EducateInteraction) => void;
}) {
  const [picked, setPicked] = useState<number | null>(null);

  const choose = (index: number) => {
    if (picked !== null) return;
    setPicked(index);
    onChoice({
      unitId: unit.id,
      kind: 'choice',
      answer: unit.choices[index].label,
      isCorrect: null
    });
  };

  const chosen = picked === null ? null : unit.choices[picked];

  return (
    <div className="space-y-3">
      {unit.choices.map((choice, index) => (
        <button
          key={choice.label}
          type="button"
          disabled={picked !== null}
          onClick={() => choose(index)}
          className={`block w-full rounded-md border px-3 py-2 text-left text-sm transition ${
            picked === index
              ? 'border-brand-500 bg-brand-50 dark:bg-brand-950/40'
              : picked !== null
                ? 'border-slate-200 opacity-50 dark:border-slate-700'
                : 'border-slate-200 hover:border-brand-400 dark:border-slate-700'
          }`}
        >
          {choice.label}
        </button>
      ))}

      {chosen && (
        <div className="space-y-3 rounded-lg bg-slate-50 p-4 dark:bg-slate-900/60">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              What happens
            </p>
            <Prose>{chosen.consequence}</Prose>
          </div>
          <button
            type="button"
            onClick={() => onBranch(chosen.nextNodeId ? `node-${chosen.nextNodeId}` : null)}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            {chosen.nextNodeId ? 'Continue' : 'Finish module'}
          </button>
        </div>
      )}
    </div>
  );
}
